/**
 * Maps point samples + user parameters to raw gaussian splat attributes
 * (INRIA PLY conventions, as expected by the editor's loader):
 *
 * - color:   f_dc_i = (c_i - 0.5) / SH_C0  (spherical harmonics DC term)
 * - opacity: logit(alpha)
 * - scale:   ln(size) per local axis
 * - rot:     quaternion, rot_0 = w
 */

import { createRng, createGaussianRng } from './rng';
import { SampleSet, ShapeParams, SplatBuffers } from './types';

const SH_C0 = 0.28209479177387814;

// how much of the sample spacing a splat radius covers in 'auto' mode: the
// ~1 sigma opaque cores of neighboring splats just touch
const AUTO_SIZE_FACTOR = 0.5;

// oriented (flattened) splats: local z scale relative to the in-plane scale
const FLAT_RATIO = 0.1;

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const logit = (alpha: number) => {
    const a = clamp(alpha, 1e-4, 1 - 1e-4);
    return Math.log(a / (1 - a));
};

const colorToDc = (c: number) => (clamp(c, 0, 1) - 0.5) / SH_C0;

interface GaussianOptions {
    // orient splats to the surface normal and flatten them along it. only
    // effective when the sample set carries normals.
    flatSplats?: boolean;
}

const buildSplats = (samples: SampleSet, params: ShapeParams, options: GaussianOptions = {}): SplatBuffers => {
    const { count, positions, normals, spacing } = samples;

    const rng = createRng(((params.seed ?? 42) ^ 0x9E3779B9) >>> 0);
    const gauss = createGaussianRng(rng);

    const baseSize = params.splatSize === 'auto' || params.splatSize === undefined ?
        spacing * AUTO_SIZE_FACTOR :
        params.splatSize;

    const sizeJitter = params.sizeJitter ?? 0;
    const colorJitter = params.colorJitter ?? 0;
    const flat = !!options.flatSplats && !!normals;

    const buffers: SplatBuffers = {
        count,
        x: new Float32Array(count),
        y: new Float32Array(count),
        z: new Float32Array(count),
        f_dc_0: new Float32Array(count),
        f_dc_1: new Float32Array(count),
        f_dc_2: new Float32Array(count),
        opacity: new Float32Array(count),
        scale_0: new Float32Array(count),
        scale_1: new Float32Array(count),
        scale_2: new Float32Array(count),
        rot_0: new Float32Array(count),
        rot_1: new Float32Array(count),
        rot_2: new Float32Array(count),
        rot_3: new Float32Array(count)
    };

    const opacity = logit(params.opacity);

    for (let i = 0; i < count; i++) {
        buffers.x[i] = positions[i * 3];
        buffers.y[i] = positions[i * 3 + 1];
        buffers.z[i] = positions[i * 3 + 2];

        // splat size, optionally log-normal jittered
        const size = sizeJitter > 0 ? baseSize * Math.exp(gauss() * sizeJitter * 0.6) : baseSize;
        const logSize = Math.log(Math.max(size, 1e-6));

        // color, optionally brightness jittered
        let { r, g, b } = params.color;
        if (colorJitter > 0) {
            const f = 1 + (rng() - 0.5) * colorJitter;
            r = clamp(r * f, 0, 1);
            g = clamp(g * f, 0, 1);
            b = clamp(b * f, 0, 1);
        }
        buffers.f_dc_0[i] = colorToDc(r);
        buffers.f_dc_1[i] = colorToDc(g);
        buffers.f_dc_2[i] = colorToDc(b);

        buffers.opacity[i] = opacity;

        if (flat) {
            // flatten along local z and rotate local z onto the surface normal
            buffers.scale_0[i] = logSize;
            buffers.scale_1[i] = logSize;
            buffers.scale_2[i] = Math.log(Math.max(size * FLAT_RATIO, 1e-6));

            const nx = normals[i * 3];
            const ny = normals[i * 3 + 1];
            const nz = normals[i * 3 + 2];

            // quaternion rotating (0,0,1) onto (nx,ny,nz)
            if (nz > 1 - 1e-6) {
                buffers.rot_0[i] = 1;  // identity, rot_0 = w
            } else if (nz < -1 + 1e-6) {
                buffers.rot_1[i] = 1;  // 180 degrees about x
            } else {
                const w = 1 + nz;
                const x = -ny;
                const y = nx;
                const len = Math.sqrt(w * w + x * x + y * y);
                buffers.rot_0[i] = w / len;
                buffers.rot_1[i] = x / len;
                buffers.rot_2[i] = y / len;
            }
        } else {
            // isotropic: rotation irrelevant, use identity
            buffers.scale_0[i] = logSize;
            buffers.scale_1[i] = logSize;
            buffers.scale_2[i] = logSize;
            buffers.rot_0[i] = 1;
        }
    }

    return buffers;
};

export { buildSplats, GaussianOptions, SH_C0 };
